import { ThemeEvents } from '@theme/events';

const CONSENT_KEY = 'Widerrufsverzicht digitale Inhalte';
const CONSENT_VALUE =
  'Ja: Zustimmung zum Beginn vor Ablauf der Widerrufsfrist, Kenntnis vom Erlöschen des Widerrufsrechts bestätigt';
/** Kassen-Knopf auf der Warenkorbseite (Horizon) und im TestHelden-Drawer (th-korb-fuss). */
const CHECKOUT_SELECTOR = '.cart__checkout-button, [data-th-consent-kasse]';

/**
 * Mandatory consent for the immediate provision of digital content (§ 356 Abs. 5 BGB).
 *
 * The "ausdrückliches Verlangen" must be fresh per purchase attempt:
 * - The checkbox is NEVER pre-ticked from the stored cart attribute. The attribute
 *   only documents the consent of the current attempt on the resulting order
 *   (cart-summary renders the checkbox unchecked unconditionally).
 * - `sessionConsent` (module state, below) is the only thing that may re-tick the
 *   box: it mirrors what the visitor did in THIS page visit and re-applies it when
 *   the drawer markup is morphed/re-rendered. It never survives a reload.
 * - When the cart is emptied, the leftover attribute is deleted (module-level
 *   cart:update listener, below) so a returning visitor starts a fresh attempt.
 */

/** Whether the visitor ticked the consent box during this page visit. */
let sessionConsent = false;

/** @type {AbortController | null} */
let clearAttributeController = null;

/** @returns {string} */
function cartUpdateUrl() {
  return (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js';
}

/**
 * Ends the current purchase attempt when the cart runs empty: forgets the
 * in-page consent and deletes the persisted cart attribute so the checkbox
 * cannot come back pre-ticked on the next attempt. Lives at module level
 * because the <cart-consent> element itself is removed from the DOM together
 * with the cart summary when the empty-cart template is swapped in.
 */
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {CustomEvent & { detail: any }} */ (event).detail;
  const itemCount = detail?.data?.itemCount ?? detail?.resource?.item_count;
  if (itemCount !== 0) return;

  sessionConsent = false;

  // Skip the request when the cart response proves the attribute is already gone.
  const attributes = detail?.resource?.attributes;
  if (attributes && !attributes[CONSENT_KEY]) return;

  clearAttributeController?.abort();
  clearAttributeController = new AbortController();

  const cleared = {};
  cleared[CONSENT_KEY] = '';

  fetch(cartUpdateUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: cleared }),
    signal: clearAttributeController.signal,
  }).catch(() => {
    /* Non-blocking: the attribute is also rewritten from the checkbox on the next change. */
  });
});

/**
 * Progressive enhancement layer on top of the native gate:
 * - The checkbox itself is `required` and `form="cart-form"`, so without JS the browser
 *   blocks the checkout submission until it is ticked (legal backstop).
 * - This component adds the live `aria-disabled` state on the checkout button, the inline
 *   `role="alert"` error, gating of the accelerated checkout buttons, and persistence of
 *   the consent as a cart attribute (so it shows up as a note on the order in the admin).
 *
 * Listeners are attached to the host element (not the inner controls) so they survive the
 * cart-items-component re-rendering/morphing its contents after quantity or line changes.
 * A MutationObserver re-applies the in-page consent after such morphs, because the
 * server-rendered replacement markup is always unchecked/pending.
 */
class CartConsent extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  #restoreQueued = false;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange, true);
    this.addEventListener('click', this.#handleClick, true);

    this.#observer = new MutationObserver(() => this.#queueRestore());
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'class', 'aria-disabled'],
    });

    this.#restore();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange, true);
    this.removeEventListener('click', this.#handleClick, true);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#fetchController?.abort();
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #checkbox() {
    return this.querySelector('.cart-consent__checkbox');
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const checkbox = this.#checkbox();
    if (!checkbox || event.target !== checkbox) return;

    sessionConsent = checkbox.checked;
    this.#syncState();
    this.#persist(checkbox.checked);
  };

  /**
   * Intercepts clicks on the checkout / accelerated buttons while consent is missing.
   * Capture phase + stopImmediatePropagation prevents the native submit and any
   * accelerated-checkout handlers from firing.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const checkbox = this.#checkbox();
    if (!checkbox || checkbox.checked) return;

    // composedPath() is robust where event.target gets retargeted or the click
    // lands on a child that is re-rendered mid-dispatch (drawer morphing).
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const checkout = this.querySelector(CHECKOUT_SELECTOR);
    const accelerated = this.querySelector('.additional-checkout-buttons');
    const inPath = (el) =>
      el instanceof Node && path.some((n) => n === el || (n instanceof Node && el.contains(n)));
    if (!inPath(checkout) && !inPath(accelerated)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError();

    // Bring the checkbox into view (drawer summaries can be scrolled) and make
    // the miss unmistakable: focus + a short attention shake on the field.
    const reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      checkbox.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    } catch {
      /* older engines: focus() below still scrolls */
    }
    checkbox.focus();

    const field = this.querySelector('.cart-consent__field');
    if (field && !reduceMotion) {
      field.classList.remove('cart-consent__field--attention');
      void field.offsetWidth; // restart the animation on repeated clicks
      field.classList.add('cart-consent__field--attention');
      field.addEventListener(
        'animationend',
        () => field.classList.remove('cart-consent__field--attention'),
        { once: true }
      );
    }
  };

  /**
   * Debounced through a microtask so a burst of mutations from one morph
   * results in a single restore, after the morph has finished.
   */
  #queueRestore() {
    if (this.#restoreQueued) return;
    this.#restoreQueued = true;
    queueMicrotask(() => {
      this.#restoreQueued = false;
      if (this.isConnected) this.#restore();
    });
  }

  /**
   * Re-applies the in-page consent state after (re-)connects and morphs. The
   * server always renders the checkbox unchecked and the gate pending, so
   * without this a quantity change would silently wipe a given consent.
   */
  #restore() {
    const checkbox = this.#checkbox();
    if (!checkbox) return;

    if (checkbox.checked !== sessionConsent) {
      checkbox.checked = sessionConsent;
    }
    this.#syncState();
  }

  /**
   * All writes are guarded (only touch the DOM when the value actually differs)
   * so the MutationObserver above never observes its own restore work.
   */
  #syncState() {
    const checkbox = this.#checkbox();
    if (!checkbox) return;

    const consented = checkbox.checked;
    if (this.classList.contains('cart-consent--pending') === consented) {
      this.classList.toggle('cart-consent--pending', !consented);
    }

    const checkout = this.querySelector(CHECKOUT_SELECTOR);
    if (checkout) {
      if (consented && checkout.hasAttribute('aria-disabled')) {
        checkout.removeAttribute('aria-disabled');
      } else if (!consented && checkout.getAttribute('aria-disabled') !== 'true') {
        checkout.setAttribute('aria-disabled', 'true');
      }
    }

    if (consented) this.#hideError();
  }

  #showError() {
    const error = this.querySelector('.cart-consent__error');
    if (!error) return;
    error.classList.remove('hidden');

    // role="alert" announces reliably only when content is (re-)inserted while
    // the node is visible — clear-then-set so repeated clicks announce again.
    const text = error.querySelector('.cart-consent__error-text');
    if (text) {
      const message = text.textContent;
      text.textContent = '';
      requestAnimationFrame(() => {
        text.textContent = message;
      });
    }
  }

  #hideError() {
    const error = this.querySelector('.cart-consent__error');
    if (error && !error.classList.contains('hidden')) {
      error.classList.add('hidden');
    }
  }

  /**
   * Persists the consent as a cart attribute so it travels with the order.
   * Quietly updates the cart without triggering a re-render.
   * @param {boolean} consented
   */
  #persist(consented) {
    const attributes = {};
    attributes[CONSENT_KEY] = consented ? CONSENT_VALUE : '';

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      signal: this.#fetchController.signal,
    }).catch(() => {
      /* Non-blocking: the consent is still submitted with the cart form on checkout. */
    });
  }
}

if (!customElements.get('cart-consent')) {
  customElements.define('cart-consent', CartConsent);
}
